import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, FileSpreadsheet, Search, Lock } from "lucide-react";
import { toast } from "sonner";
import { Q, hoyGT, fotoUrl } from "./util";

export const AnticiposHoja = () => {
  const h = hoyGT();
  const [desde, setDesde] = useState(`${h.slice(0, 8)}01`);
  const [hasta, setHasta] = useState(h);
  const [q, setQ] = useState("");
  const [banco, setBanco] = useState("");
  const [d, setD] = useState(null);
  const seq = useRef(0);
  const params = { desde, hasta, q, banco };
  const load = useCallback(() => {
    const n = ++seq.current;
    api.get("/mi-dinero/anticipos", { params: { desde, hasta, q, banco } }).then(({ data }) => { if (n === seq.current) setD(data); }).catch((e) => toast.error(formatApiError(e)));
  }, [desde, hasta, q, banco]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const excel = async () => {
    try {
      const { data } = await api.get("/mi-dinero/anticipos.xlsx", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data); a.download = `anticipos_${desde}_${hasta}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="anticipos-hoja">
      <div className="text-xs text-zinc-600 flex items-center gap-1"><Lock className="w-3 h-3" />Solo consulta: los anticipos se registran y modifican únicamente en Hoja de Pedidos.</div>
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="ant-desde" />
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="ant-hasta" />
        <select value={banco} onChange={(e) => setBanco(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="ant-banco">
          <option value="">Todos los bancos</option>{(d?.opciones_banco || []).map((b) => <option key={b}>{b}</option>)}<option>Otros</option>
        </select>
        <div className="relative"><Search className="absolute left-2 top-2 w-4 h-4 text-zinc-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pedido o cliente" className="border rounded-lg pl-7 pr-2 py-1.5 text-sm" data-testid="ant-buscar" /></div>
        <Button variant="outline" onClick={() => window.print()} className="ml-auto" data-testid="ant-imprimir"><Printer className="w-4 h-4 mr-1" />Imprimir</Button>
        <Button variant="outline" onClick={excel} data-testid="ant-excel"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
      </div>
      <div className="grid xl:grid-cols-[1fr_260px] gap-3 items-start">
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-xs">
            <thead className="bg-violet-700 text-white text-left"><tr>{["Pedido", "Cliente", "Fecha", "Monto", "Banco / forma de pago", "Referencia", "Comprobante", "Total pedido", "Saldo pendiente"].map((c) => <th key={c} className="px-2 py-2">{c}</th>)}</tr></thead>
            <tbody className="divide-y">
              {(d?.anticipos || []).map((r) => (
                <tr key={r.numero} data-testid={`ant-fila-${r.numero}`}>
                  <td className="px-2 py-1 font-bold">{r.numero}</td><td className="px-2">{r.cliente}</td><td className="px-2 whitespace-nowrap">{r.fecha}</td>
                  <td className="px-2 text-right font-bold whitespace-nowrap"><Q v={r.monto} /></td><td className="px-2">{r.banco}{r.metodo && r.metodo !== r.banco ? ` · ${r.metodo}` : ""}</td>
                  <td className="px-2">{r.referencia}{r.factura && <div className="text-[10px] text-zinc-500">{r.factura}</div>}</td>
                  <td className="px-2">{r.comprobante ? <a href={fotoUrl(r.comprobante)} target="_blank" rel="noreferrer"><img src={fotoUrl(r.comprobante)} alt="" className="w-10 h-8 object-cover rounded border" /></a> : "—"}</td>
                  <td className="px-2 text-right whitespace-nowrap"><Q v={r.total} /></td><td className="px-2 text-right font-bold whitespace-nowrap"><Q v={r.saldo} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-zinc-100 font-black" data-testid="ant-totales">
              <tr><td className="px-2 py-2" colSpan={3}>TOTAL ({d?.anticipos?.length || 0})</td><td className="px-2 text-right"><Q v={d?.total} /></td><td colSpan={4} /><td className="px-2 text-right"><Q v={d?.saldo_total} /></td></tr>
            </tfoot>
          </table>
        </div>
        <div className="rounded-xl border bg-white p-3" data-testid="ant-bancos">
          <div className="font-black text-sm mb-2">Resumen por bancos</div>
          {(d?.bancos || []).map((b) => <div key={b.banco} className="flex justify-between text-sm py-1 border-b"><span>{b.banco}</span><b><Q v={b.total} /></b></div>)}
          <div className="flex justify-between text-sm pt-2 font-black"><span>Total</span><Q v={d?.total} /></div>
        </div>
      </div>
    </div>
  );
};
